import type { AppConfig } from './config';
import { codeChallenge, randomToken } from './pkce';

const VERIFIER_KEY = 'aiwb.pkce_verifier';
const STATE_KEY = 'aiwb.oauth_state';
const RETURN_KEY = 'aiwb.return_to';
const REFRESH_KEY = 'aiwb.refresh_token';

export interface Tokens {
  /** Sent as the bearer token. The API Gateway JWT authorizer validates the
   *  `aud` claim against the app client id, which only the id token carries. */
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  /** Epoch milliseconds. */
  expiresAt: number;
}

export interface Identity {
  /** Cognito `sub` — used as the {uid} path segment in every user-scoped API call. */
  sub: string;
  email?: string;
  username?: string;
}

export class AuthError extends Error {}

/** The registered Cognito callback URL is the bare origin (lib/stack.ts:52-55). */
export function redirectUri(): string {
  return window.location.origin;
}

export function decodeIdToken(idToken: string): Identity {
  const payload = idToken.split('.')[1];
  if (!payload) throw new AuthError('Malformed id token');
  const base64 = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '=');
  const bytes = Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));
  const claims = JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
  const sub = claims.sub;
  if (typeof sub !== 'string') throw new AuthError('Id token has no subject');
  return {
    sub,
    email: typeof claims.email === 'string' ? claims.email : undefined,
    username: typeof claims['cognito:username'] === 'string' ? (claims['cognito:username'] as string) : undefined,
  };
}

export async function startSignIn(config: AppConfig, returnTo: string): Promise<void> {
  const verifier = randomToken();
  const state = randomToken();
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(RETURN_KEY, returnTo);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.cognitoClientId,
    redirect_uri: redirectUri(),
    scope: config.scopes,
    state,
    code_challenge_method: 'S256',
    code_challenge: await codeChallenge(verifier),
  });
  window.location.assign(`https://${config.cognitoDomain}/oauth2/authorize?${params}`);
}

interface TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_in: number;
}

async function tokenRequest(config: AppConfig, body: URLSearchParams): Promise<Tokens> {
  const response = await fetch(`https://${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body,
  });
  const text = await response.text();
  if (!response.ok) {
    throw new AuthError(`Token endpoint returned HTTP ${response.status}: ${text}`);
  }
  const payload = JSON.parse(text) as TokenResponse;
  return {
    idToken: payload.id_token,
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token,
    expiresAt: Date.now() + payload.expires_in * 1000,
  };
}

/** Result of consuming an OAuth redirect back onto the app's origin. */
export interface CallbackResult {
  tokens: Tokens;
  returnTo: string;
}

export function readCallback(): { code?: string; state?: string; error?: string } {
  const params = new URLSearchParams(window.location.search);
  return {
    code: params.get('code') ?? undefined,
    state: params.get('state') ?? undefined,
    error: params.get('error_description') ?? params.get('error') ?? undefined,
  };
}

export function clearCallbackParams(): void {
  history.replaceState({}, document.title, window.location.pathname);
}

export async function completeSignIn(config: AppConfig, code: string, state: string): Promise<CallbackResult> {
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const returnTo = sessionStorage.getItem(RETURN_KEY) ?? '/boxes';
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  sessionStorage.removeItem(RETURN_KEY);

  if (!expectedState || state !== expectedState) {
    throw new AuthError('Sign-in state did not match. Start a new sign-in.');
  }
  if (!verifier) {
    throw new AuthError('The PKCE verifier is missing. Start a new sign-in.');
  }

  const tokens = await tokenRequest(
    config,
    new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.cognitoClientId,
      code,
      code_verifier: verifier,
      redirect_uri: redirectUri(),
    }),
  );
  return { tokens, returnTo };
}

export async function refreshTokens(config: AppConfig, refreshToken: string): Promise<Tokens> {
  const tokens = await tokenRequest(
    config,
    new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.cognitoClientId,
      refresh_token: refreshToken,
    }),
  );
  // Cognito omits the refresh token on a refresh — keep the one we have.
  return { ...tokens, refreshToken: tokens.refreshToken ?? refreshToken };
}

/** Ends the Cognito session itself and returns the browser to the app. The
 *  return URL must be registered as a logout URL on the app client. */
export function hostedLogoutUrl(config: AppConfig): string {
  const params = new URLSearchParams({
    client_id: config.cognitoClientId,
    logout_uri: redirectUri(),
  });
  return `https://${config.cognitoDomain}/logout?${params}`;
}

/** Invalidates the refresh token server-side, so a copy left behind in storage
 *  is worthless. Best effort — sign-out proceeds either way. */
export async function revokeRefreshToken(config: AppConfig, refreshToken: string): Promise<void> {
  await fetch(`https://${config.cognitoDomain}/oauth2/revoke`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      token: refreshToken,
      client_id: config.cognitoClientId,
    }),
  });
}

export function storedRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function storeRefreshToken(token: string | undefined): void {
  if (token) localStorage.setItem(REFRESH_KEY, token);
}

export function forgetRefreshToken(): void {
  localStorage.removeItem(REFRESH_KEY);
}
